/**
 * useSharedInView
 *
 * React hook for efficient intersection observation using shared IntersectionObserver instances.
 *
 * - Provides a ref and inView boolean for tracking element visibility in the viewport.
 * - Uses a shared observer pool keyed by observer ID for performance (reduces observer count).
 * - Supports multiple observer configurations (threshold, rootMargin, etc) via observerConfigs.
 * - Used for scroll-based animations, lazy loading, and triggering effects when elements enter/exit view.
 *
 * Usage:
 * ```tsx
 * const { ref, inView } = useSharedInView<HTMLDivElement>("fadeIn25");
 *
 * return <div ref={ref}>{inView ? "Visible" : "Hidden"}</div>;
 * ```
 *
 * ObserverId options:
 * - "default": threshold 0
 * - "half": threshold 0.5
 * - "full": threshold 1
 * - "fadeIn25": rootMargin "0px 0px -25% 0px", threshold 0.1
 * - "fadeIn10": rootMargin "0px 0px -10% 0px", threshold 0.1
 * - "animateText": rootMargin "0px 0px -15% 0px", threshold 0.1
 *
 * Options:
 * - `once`: when true, `inView` latches to true on first entry and the element
 *   is unobserved — so the value never returns to false. Use for play-once
 *   scroll animations (FadeIn, AnimateText) that must not re-trigger on
 *   scroll-out/scroll-in. Defaults to false (live visibility toggling).
 *
 * Returns:
 *   - ref: React ref to attach to the element
 *   - inView: boolean (true if element is in view)
 *
 * See also:
 * - https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

import { useEffect, useRef, useState } from "react"

export const observerConfigs = {
  default: {
    root: null,
    rootMargin: "0px",
    threshold: 0,
  },
  half: {
    root: null,
    rootMargin: "0px",
    threshold: 0.5,
  },
  full: {
    root: null,
    rootMargin: "0px 0px -1px 0px",
    threshold: 1,
  },
  fadeIn25: {
    root: null,
    rootMargin: "0px 0px -25% 0px",
    threshold: 0.1,
  },
  fadeIn10: {
    root: null,
    rootMargin: "0px 0px -10% 0px",
    threshold: 0.1,
  },
  animateText: {
    root: null,
    rootMargin: "0px 0px -15% 0px",
    threshold: 0.1,
  },
}

export type ObserverId = keyof typeof observerConfigs

// Map of observer instances keyed by ID
const observers = new Map<ObserverId, IntersectionObserver>()
const listeners = new Map<Element, (inView: boolean) => void>()

const getObserver = (id: ObserverId, options: IntersectionObserverInit) => {
  if (!observers.has(id)) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const cb = listeners.get(entry.target)
        if (cb) cb(entry.isIntersecting)
      }
    }, options)
    observers.set(id, observer)
  }
  return observers.get(id)!
}

export const useSharedInView = <T extends Element>(
  id: ObserverId = "default",
  options: { once?: boolean } = {},
) => {
  const { once = false } = options
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)
  const hasLatchedRef = useRef(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (once && hasLatchedRef.current) return // already played; don't re-observe

    const observer = getObserver(id, observerConfigs[id])
    const handle = (isIntersecting: boolean) => {
      if (once) {
        if (!isIntersecting) return
        hasLatchedRef.current = true
        setInView(true)
        listeners.delete(node)
        observer.unobserve(node) // stop watching after first entry
        return
      }
      setInView(isIntersecting)
    }
    listeners.set(node, handle)
    observer.observe(node)

    return () => {
      listeners.delete(node)
      observer.unobserve(node)
    }
  }, [id, once])

  return { ref, inView }
}
